import React, { useState, useEffect } from 'react';
import { Doughnut, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement
} from 'chart.js';
import DateRangeFilter from '../components/DateRangeFilter';
import LoadingOverlay from '../components/LoadingOverlay';
import { fetchSeriesPerLanguage, fetchSeriesPerReadingLevel, fetchRecentCreatedSeries } from '../services/api';
import { getLevelColors, getLanguageColors } from '../utils/chartColors';
import { formatCompactNumber } from '../utils/formatters';
import './DataPages.css';
import './Pages.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const SeriesOverview = ({ isDarkMode }) => {
  const toUTCDateStr = (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

  const getDefaultRange = () => {
    const end = new Date();
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - 29);
    return {
      startDate: toUTCDateStr(start),
      endDate: toUTCDateStr(end)
    };
  };

  const [startDate, setStartDate] = useState(getDefaultRange().startDate);
  const [endDate, setEndDate] = useState(getDefaultRange().endDate);
  const [languageData, setLanguageData] = useState([]);
  const [readingLevelData, setReadingLevelData] = useState([]);
  const [recentSeriesData, setRecentSeriesData] = useState([]);
  const [earliestDate, setEarliestDate] = useState(null);
  const [latestDate, setLatestDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadData = async (start, end, isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const [perLanguageResult, perReadingLevel, recentSeries] = await Promise.all([
        fetchSeriesPerLanguage(start, end),
        fetchSeriesPerReadingLevel(start, end),
        fetchRecentCreatedSeries(start, end, 5)
      ]);
      setLanguageData(perLanguageResult.items || []);
      if (perLanguageResult.earliestDate) setEarliestDate(perLanguageResult.earliestDate);
      if (perLanguageResult.latestDate) setLatestDate(perLanguageResult.latestDate);
      setReadingLevelData(perReadingLevel);
      setRecentSeriesData(recentSeries);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const { startDate: start, endDate: end } = getDefaultRange();
    loadData(start, end, false);
  }, []);

  const handleApply = (directStartDate = null, directEndDate = null) => {
    const fetchStartDate = directStartDate ?? startDate;
    const fetchEndDate = directEndDate ?? endDate;
    loadData(fetchStartDate, fetchEndDate, true);
  };

  const getPeriodLabel = () => {
    if (!startDate || !endDate) return 'All Time';
    const s = new Date(startDate);
    const e = new Date(endDate);
    const days = Math.ceil(Math.abs(e - s) / (1000 * 60 * 60 * 24)) + 1;
    return `${days} Days`;
  };

  const textColor = isDarkMode ? '#e5e7eb' : '#333333';

  // Language - Pie Chart
  const languageLabels = languageData.map(item => item.languageCode || 'Unknown');
  const languageChartData = {
    labels: languageLabels,
    datasets: [{
      data: languageData.map(item => item.count),
      backgroundColor: getLanguageColors(languageLabels, isDarkMode),
      borderColor: isDarkMode ? '#1f2937' : '#ffffff',
      borderWidth: 3,
      hoverBorderWidth: 4,
      hoverOffset: 15
    }]
  };

  const languageChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'right',
        labels: {
          color: textColor,
          padding: isMobile ? 12 : 16,
          font: { size: isMobile ? 11 : 12, family: "'Inter', sans-serif" },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(51, 51, 51, 0.95)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#af3eff',
        borderWidth: 1,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `Series: ${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  // Reading Level - Doughnut Chart
  const readingLevelChartData = {
    labels: readingLevelData.map(item => `Level ${item.level}` || 'Unknown'),
    datasets: [{
      data: readingLevelData.map(item => item.count),
      backgroundColor: getLevelColors(readingLevelData.map(item => item.level), isDarkMode),
      borderColor: isDarkMode ? '#1f2937' : '#ffffff',
      borderWidth: 2,
      hoverOffset: 8
    }]
  };

  const readingLevelChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'right',
        labels: {
          color: textColor,
          padding: isMobile ? 12 : 16,
          font: { size: isMobile ? 11 : 12 },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(51, 51, 51, 0.95)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        displayColors: false,
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `Series: ${context.parsed.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  const totalLanguageSeries = languageData.reduce((sum, item) => sum + item.count, 0);
  const totalReadingLevelSeries = readingLevelData.reduce((sum, item) => sum + item.count, 0);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  return (
    <div className="page-container">
      {refreshing && <LoadingOverlay />}

      <div className="page-header">
        <div className="page-header-content">
          <h1>Series Overview</h1>
          <div className="page-header-badges">
            <div className="control-badge">
              <span className="badge-label">Period</span>
              <span className="badge-value">{getPeriodLabel()}</span>
            </div>
            {!loading && (
              <div className="control-badge">
                <span className="badge-label">Total Series</span>
                <span className="badge-value">{totalLanguageSeries.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="data-page-content">
        <div className="filters-container">
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onApply={handleApply}
            earliestDate={earliestDate}
            latestDate={latestDate}
            showMax={true}
          />
        </div>

        {error && <div className="table-empty">Error: {error}</div>}
        {loading && <div className="table-loading">Loading ...</div>}

        {!loading && !error && (
          <>
            <div className="charts-grid">
              {languageData.length > 0 && (
                <div className="data-page-chart-card">
                  <div className="chart-header">
                    <h3>Series by Language</h3>
                    <span className="chart-badge">
                      {formatCompactNumber(totalLanguageSeries)} series
                    </span>
                  </div>
                  <div className="chart-wrapper chart-wrapper-tall">
                    <Pie key={`language-${isDarkMode}`} data={languageChartData} options={languageChartOptions} />
                  </div>
                </div>
              )}

              {readingLevelData.length > 0 && (
                <div className="data-page-chart-card">
                  <div className="chart-header">
                    <h3>Series by Reading Level</h3>
                    <span className="chart-badge">
                      {formatCompactNumber(totalReadingLevelSeries)} series
                    </span>
                  </div>
                  <div className="chart-wrapper chart-wrapper-tall">
                    <Doughnut key={`readingLevel-${isDarkMode}`} data={readingLevelChartData} options={readingLevelChartOptions} />
                  </div>
                </div>
              )}

              {recentSeriesData.length > 0 && (
                <div className="data-page-chart-card">
                  <div className="chart-header">
                    <h3>Latest Created Series</h3>
                    <span className="chart-badge">
                      Last {recentSeriesData.length}
                    </span>
                  </div>
                  <div className="recent-series-list">
                    {recentSeriesData.map((series, index) => (
                      <div key={series.seriesId} className="recent-series-item">
                        <div className="recent-series-rank">{index + 1}</div>
                        <div className="recent-series-info">
                          <div className="recent-series-title">{series.seriesTitle}</div>
                          <div className="recent-series-meta">
                            <span>{series.language || 'Unknown'}</span>
                            <span className="meta-separator">•</span>
                            <span>{series.bookCount} books</span>
                            <span className="meta-separator">•</span>
                            <span>{formatDate(series.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {!loading && !error && languageData.length === 0 && (
          <div className="table-empty">
            No series data available for the selected period.
          </div>
        )}
      </div>
    </div>
  );
};

export default SeriesOverview;
